import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminReview = () => {
  const { toast } = useToast();
  const [isApproving, setIsApproving] = useState(false);

  const { data: pendingResources = [], refetch: refetchResources } = useQuery({
    queryKey: ['pending-resources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pending_resources')
        .select('*')
        .eq('status', 'pending');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: pendingDonations = [], refetch: refetchDonations } = useQuery({
    queryKey: ['pending-donations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pending_donation_resources')
        .select('*')
        .eq('status', 'pending');
      
      if (error) throw error;
      return data;
    }
  });

  const approveResource = async (submission: any) => {
    setIsApproving(true);
    try {
      // Insert into resources table
      const { error: insertError } = await supabase
        .from('resources')
        .insert({
          name: submission.name,
          type: submission.type.toLowerCase(),
          description: submission.offer || '',
          address: submission.address,
          availability: 'available'
        });

      if (insertError) throw insertError;

      // Update status in pending_resources
      const { error: updateError } = await supabase
        .from('pending_resources')
        .update({ status: 'approved' })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      toast({
        title: "Resource Approved",
        description: "The submission has been approved and added to the resources list."
      });

      refetchResources();
    } catch (error) {
      console.error('Error approving submission:', error);
      toast({
        title: "Error",
        description: "Failed to approve the submission. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsApproving(false);
    }
  };

  const approveDonation = async (submission: any) => {
    setIsApproving(true);
    try {
      // Insert into donation_resources table
      const { error: insertError } = await supabase
        .from('donation_resources')
        .insert({
          name: submission.name,
          type: submission.type.toLowerCase(),
          description: submission.offer || '',
          address: submission.address,
          availability: 'available'
        });

      if (insertError) throw insertError;

      // Update status in pending_donation_resources
      const { error: updateError } = await supabase
        .from('pending_donation_resources')
        .update({ status: 'approved' })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      toast({
        title: "Donation Approved",
        description: "The donation has been approved and added to the donations list."
      });

      refetchDonations();
    } catch (error) {
      console.error('Error approving donation:', error);
      toast({
        title: "Error",
        description: "Failed to approve the donation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline">
          Review Submissions ({pendingResources.length + pendingDonations.length})
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[800px] sm:max-w-[800px]">
        <SheetHeader>
          <SheetTitle>Pending Submissions</SheetTitle>
        </SheetHeader>
        <Tabs defaultValue="resources" className="mt-4">
          <TabsList>
            <TabsTrigger value="resources">Resources ({pendingResources.length})</TabsTrigger>
            <TabsTrigger value="donations">Donations ({pendingDonations.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="resources">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingResources.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>{submission.name}</TableCell>
                    <TableCell>{submission.type}</TableCell>
                    <TableCell>{submission.address}</TableCell>
                    <TableCell>{submission.details}</TableCell>
                    <TableCell>
                      <Button
                        onClick={() => approveResource(submission)}
                        disabled={isApproving}
                      >
                        Approve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {pendingResources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      No pending resource submissions
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>
          <TabsContent value="donations">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingDonations.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>{submission.name}</TableCell>
                    <TableCell>{submission.type}</TableCell>
                    <TableCell>{submission.address}</TableCell>
                    <TableCell>{submission.details}</TableCell>
                    <TableCell>
                      <Button
                        onClick={() => approveDonation(submission)}
                        disabled={isApproving}
                      >
                        Approve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {pendingDonations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      No pending donation submissions
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default AdminReview;